import React, {useContext, useEffect} from 'react';
import {observer} from 'mobx-react-lite';
import {
    IButtonProps,
    IDataInfoProps,
    IFilterProps,
    ITableRowProps,
    ModalProvider,
    TemplateQuestions,
    TFilterValue,
    Toggle,
} from '../../../patterns';
import {StoreContext} from '../base/root-store';
import {
    CatalogQuestionsWithCategory,
    Languages,
    QuestionsStore,
    QuestionsTab,
    SingleAdHocQuestion,
    SingleCatalogQuestion,
} from './questions-store';
import {
    ITemplateQuestionsAction,
    ITemplateQuestionsRowProps,
    ITemplateQuestionsTableProps,
} from '../../../patterns/templates/questions/questions';
import {WebappBase} from '../dashboard/WebappBase';
import {AdHocQuestionModal} from './AdHocQuestionModal';
import {formatToDayMonthAndYear, isDateInFuture} from '../base/dateUtils';
import {UserStore} from '../user/user-store';
import {QuestionsPageHelpModal} from './QuestionsPageHelpModal';
import {CatalogQuestionModal} from './CatalogQuestionModal';
import {CustomCategoryModal} from '../categories/CustomCategoryModal';
import {getCategoryName, isCustomCategory} from '../categories/CategoriesUtil';
import {DisableCategoryModal} from '../categories/DisableCategoryModal';
import Button from '../../../patterns/components/button/button';
import {DisableQuestionModal} from '../categories/DisableQuestionModal';


export const Questions = observer((): JSX.Element => {
    const store = useContext(StoreContext).questionStore;
    const userStore = useContext(StoreContext).userStore;

    useEffect(() => {
        store.onEnter();
    }, []);

    const questionsPageHelpModal: ITemplateQuestionsAction = {
        options: [
            {
                text: '',
                icon: 'info',
                color: 'white',
                onClick: (): void => store.openQuestionsPageHelpModal(),
            },
        ],
    };

    return (
        <WebappBase
            store={store}
            title={store.label('questions.title')}
            renderChildren={ (): React.ReactNode => (
                <React.Fragment>
                    <ModalProvider
                        isOpen={store.variables.openModal !== undefined}
                        onToggle={(): void => store.closeModal()}>
                        {store.variables.openModal === 'add-adhoc-question' && <AdHocQuestionModal/>}
                        {store.variables.openModal === 'edit-adhoc-question' && <AdHocQuestionModal/>}
                        {store.variables.openModal === 'add-catalog-question' && <CatalogQuestionModal/>}
                        {store.variables.openModal === 'edit-catalog-question' && <CatalogQuestionModal/>}
                        {store.variables.openModal === 'add-custom-category' && <CustomCategoryModal/>}
                        {store.variables.openModal === 'edit-custom-category' && <CustomCategoryModal/>}
                        {store.variables.openModal === 'disable-category' && <DisableCategoryModal/>}
                        {store.variables.openModal === 'disable-question' && <DisableQuestionModal/>}
                        {store.variables.openModal === 'question-help' && <QuestionsPageHelpModal/>}
                    </ModalProvider>
                    <TemplateQuestions
                        title={store.label('questions.title')}
                        filter={userStore.payload?.isAdmin === true || userStore.payload?.hasManagerRights === true ? questionsFilterProps(store) : undefined}
                        rightToolbarItems={{
                            actions: questionsPageHelpModal,
                            buttons: userStore.payload?.isAdmin
                                ? (store.variables.openTab === 'ad-hoc-questions'
                                    ? [addAdHocQuestionProps(store)]
                                    : [addCustomQuestionCategoryProps(store), addCatalogQuestionProps(store)])
                                : (userStore.payload?.hasManagerRights && store.variables.openTab === 'ad-hoc-questions'
                                    ? [addAdHocQuestionProps(store)]
                                    : []),
                        }}
                        table={store.variables.openTab === 'ad-hoc-questions' ? adHocQuestionsTableProps(store, userStore): catalogQuestionsTableProps(store, userStore)}
                        onboardingTable={store.variables.openTab === 'ad-hoc-questions' ? undefined : onboardingQuestionTableProps(store, userStore)}
                    />
                </React.Fragment>
            )
            }/>
    );
});

const getQuestionUsageInBusinessUnits = (
    userStore: UserStore,
    store: QuestionsStore,
    question: SingleCatalogQuestion,
): string => {
    const businessUnits = userStore.payload!.businessUnits;

    const allDisabledBusinessUnits = store.payload?.allQuestionsWithCategories.find((category) =>
        category.allCatalogQuestions.some((q) => q.id === question.id)) ?.disabledBusinessUnits || [];

    const allBusinessUnits = businessUnits.filter(
        (unit) => !allDisabledBusinessUnits.includes(unit.id));

    const allBusinessUnitsCount = allBusinessUnits.length;

    const catalogQuestionCategory = store.payload?.allQuestionsWithCategories.find(
        (c) => c.categoryId === question.categoryId);

    const allQuestionsInCategory = catalogQuestionCategory?.allCatalogQuestions || [];

    const disabledBusinessUnitsCount =
        allQuestionsInCategory.find((q) => q.id === question.id)?.disabledBusinessUnits.filter(
            (id) => allBusinessUnits.some((unit) => unit.id === id)).length || 0;

    const activeCount = allBusinessUnitsCount - disabledBusinessUnitsCount;

    return `${activeCount} / ${allBusinessUnitsCount}`;
};

const processQuestions = (
    questionsData: CatalogQuestionsWithCategory[],
    store: QuestionsStore,
    userStore: UserStore,
): ITemplateQuestionsRowProps[] => {
    const questions: ITemplateQuestionsRowProps[] = [];

    const sortedQuestions = questionsData.slice().sort((a, b) => {
        const dateA = new Date(a.createdTs);
        const dateB = new Date(b.createdTs);

        return dateA.getTime() - dateB.getTime();
    });

    sortedQuestions.forEach((catalogQuestion) => {
        const categoryId = catalogQuestion.categoryId;
        const catalogQuestions = catalogQuestion.allCatalogQuestions.map((singleQuestion: SingleCatalogQuestion): ITemplateQuestionsRowProps => ({
            id: singleQuestion.id,
            cells: [
                {
                    content: questionTextInUserLanguage(userStore, singleQuestion.questionLanguages),
                },
                {
                    content: userStore.payload?.isAdmin === true ? <Toggle
                        input={{
                            onChange: (event): void => {
                                store.onSendingActiveToggle(singleQuestion.id, event.target.checked);
                            },
                            checked: singleQuestion.requestedIsActive,
                        }}
                        id={singleQuestion.id}
                        isPending={singleQuestion.isActive !== singleQuestion.requestedIsActive}
                    />
                        : undefined,
                },
                {
                    content: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled ?
                        <Button
                            style="link"
                            block={false}
                            color="white"
                            text={getQuestionUsageInBusinessUnits(userStore, store, singleQuestion)}
                            onClick={() => store.openDisableQuestionForBusinessUnitModal(singleQuestion)}
                        /> : undefined,
                },
            ],
            actions: singleQuestion.isCustomCatalogQuestion && userStore.payload?.isAdmin === true ? {
                trigger: {
                    text: 'More',
                },
                options: [
                    {
                        text: store.label('questions.edit-question-button'),
                        iconLeft: 'settings',
                        onClick: (): void => store.openEditCatalogQuestionModal(singleQuestion, categoryId),
                    },
                    {
                        text: store.label('questions.delete-question-button'),
                        iconLeft: 'delete',
                        disabled: singleQuestion.isAsked,
                        onClick: (): void => store.deleteCatalogQuestion(singleQuestion),
                    },
                ],
            } : undefined,
            isTextGreyedOut: !singleQuestion.requestedIsActive,
        }));

        const catalogActions: ITemplateQuestionsAction | undefined = {
            trigger: {
                text: 'More',
            },
            options: store.payload?.questionCategories
                .filter((category) => category.id === catalogQuestion.categoryId)
                .map((category) => ({
                    text: store.label('questions.edit-question-button'),
                    iconLeft: 'settings',
                    onClick: (): void => {
                        store.openEditCustomCategoryModal(category);
                    },
                }))
                .concat(
                    store.payload?.questionCategories
                        .filter((category) => category.id === catalogQuestion.categoryId)
                        .map((category) => {
                            const isCategoryDisabled = catalogQuestion.allCatalogQuestions
                                .filter((question: SingleCatalogQuestion) => question.categoryId === category.id)
                                .some((question: SingleCatalogQuestion) => question.isAsked);

                            return {
                                text: store.label('questions.delete-question-button'),
                                iconLeft: 'settings',
                                disabled: isCategoryDisabled,
                                onClick: (): void => {
                                    store.deleteCustomCategory(category.id);
                                },
                            };
                        }),
                ),
        };

        const getCategoryUsageInBusinessUnits = (catId: string): string => {
            const businessUnits = userStore?.payload?.businessUnits;
            const categories = store.payload?.allQuestionsWithCategories;

            if (!businessUnits) {
                return '';
            }

            const allBusinessUnitsCount = businessUnits.length;

            const selectedCategory = categories?.find((category) => category.categoryId === catId);

            const disabledBusinessUnitsCount = selectedCategory?.disabledBusinessUnits?.length || 0;
            const activeCount = allBusinessUnitsCount - disabledBusinessUnitsCount;

            return `${activeCount} / ${allBusinessUnitsCount}`;
        };


        questions.push({
            cells: [
                {
                    content: <div> { getCategoryName(userStore.payload!.language, categoryId, store.categories) }
                        <span className="question__text-bold-green">{ ' ' + catalogQuestions.filter((q: ITableRowProps) => q.isTextGreyedOut === false).length}</span>
                        { '/' + catalogQuestions.length }
                    </div>,
                },
                {
                    content: userStore.payload?.isAdmin === true ? <Toggle
                        input={{
                            onChange: (event): void => {
                                store.onSendingActiveCategoryToggle(categoryId, event.target.checked);
                            },
                            checked: catalogQuestion.allCatalogQuestions.some((question: SingleCatalogQuestion) => question.requestedIsActive),
                        }}
                        id={categoryId}
                        isPending={catalogQuestion.allCatalogQuestions.some((question: SingleCatalogQuestion) => question.requestedIsActive !== question.isActive)}
                    /> : undefined,
                },
                {
                    content: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled ?
                        <Button style={'link'} block={false} color={'white'} text={getCategoryUsageInBusinessUnits(categoryId)} isTextBold={true}
                            onClick={(): void => store.openDisableCategoryForBusinessUnitModal(categoryId)}/> : undefined,
                },
            ],
            subRows: catalogQuestions,
            actions: userStore.payload?.isAdmin === true && isCustomCategory(categoryId) ? catalogActions : undefined,
            isTextBold: true,
        });
    });

    return questions;
};

const catalogQuestionsTableProps = (store: QuestionsStore, userStore: UserStore): ITemplateQuestionsTableProps => {
    const questions = processQuestions(store.payload!.allQuestionsWithCategories.filter((category) => !category.isOnboarding), store, userStore);

    return ({
        columns: [
            {
                header: store.label('questions.catalog-questions.sub-header.questions'),
                width: userStore.payload?.isBusinessUnitDisablingEnabled ? '60%' : '90%',
            },
            {
                header: userStore.payload?.isAdmin === true ? store.label('questions.catalog-questions.sub-header.toggle') : '',
                width: '10%',
            },
            {
                header: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled? store.label('questions.catalog-questions.sub-header.team-usage') : '',
                width: '30%',
            },
        ],
        rows: questions,
    });
};

const onboardingQuestionTableProps = (store: QuestionsStore, userStore: UserStore): ITemplateQuestionsTableProps => {
    const onboardingQuestions = processQuestions(store.payload!.allQuestionsWithCategories.filter((category) => category.isOnboarding), store, userStore);

    return ({
        columns: [
            {
                header: store.label('questions.catalog-questions.sub-header.onboarding-questions'),
                width: userStore.payload?.isBusinessUnitDisablingEnabled ? '60%' : '90%',
            },
            {
                header: userStore.payload?.isAdmin === true ? store.label('questions.catalog-questions.sub-header.toggle') : '',
                width: '10%',
            },
            {
                header: userStore.payload?.isAdmin === true && userStore.payload.isBusinessUnitDisablingEnabled? store.label('questions.catalog-questions.sub-header.team-usage') : '',
                width: '30%',
            },
        ],
        rows: onboardingQuestions,
    });
};

const adHocQuestionsTableProps = (store: QuestionsStore, userStore: UserStore): ITemplateQuestionsTableProps => {

    const businessUnitIds = userStore?.payload?.businessUnits
        ? userStore.payload.businessUnits.map((unit) => unit.id)
        : [userStore.payload?.businessUnitId];

    const rows: ITemplateQuestionsRowProps[] = store.payload!.allAdHocQuestions.filter((it) => businessUnitIds.includes(it.businessUnitId)).map((singleQuestion: SingleAdHocQuestion): ITemplateQuestionsRowProps => {

        const adHocQuestionLanguage = questionTextInUserLanguage(userStore, singleQuestion.questionLanguages);

        const editOrDeleteActions = {
            trigger: {
                text: 'More',
            },
            options: [
                {
                    text: store.label('questions.edit-question-button'),
                    iconLeft: 'settings',
                    onClick: (): void => store.openEditAdHocQuestionModal(singleQuestion),
                },
                {
                    text: store.label('questions.delete-question-button'),
                    iconLeft: 'delete',
                    onClick: (): void => store.deleteAdHocQuestion(singleQuestion),
                },
            ],
        };

        return ({
            id: singleQuestion.id,
            cells: [
                {
                    content: adHocQuestionLanguage,
                    comparableValue: adHocQuestionLanguage,
                },
                {
                    content: singleQuestion.businessUnitName,
                    comparableValue: singleQuestion.businessUnitName,
                },
                {
                    content: store.label(`questions.ad-hoc.employee-segment.${singleQuestion.employeeSegment}`),
                    comparableValue: store.label(`questions.ad-hoc.employee-segment.${singleQuestion.employeeSegment}`),
                },
                {
                    content: store.payload!.employeeLocations.length > 1 ? store.label(`common.location.${singleQuestion.employeeLocation}`) : undefined,
                    comparableValue: store.payload!.employeeLocations.length > 1 ? store.label(`common.location.${singleQuestion.employeeLocation}`) : undefined,
                },
                {
                    content: formatToDayMonthAndYear(singleQuestion.sendDate),
                    comparableValue: singleQuestion.sendDate,
                },
            ],
            actions: isDateInFuture(new Date(singleQuestion.sendDate)) ? editOrDeleteActions : undefined,
        });
    });

    return ({
        columns: [
            {
                header: store.label('questions.ad-hoc.sub-header.title'),
                width: '30%',
                isSortable: true,
            },
            {
                header: store.label('questions.ad-hoc.sub-header.target-business-unit'),
                width: '20%',
                isSortable: true,
            },
            {
                header: store.label('questions.ad-hoc.sub-header.employee-segment'),
                width: '20%',
                isSortable: true,
            },
            {
                header: store.payload!.employeeLocations.length > 1 ? store.label('questions.ad-hoc.sub-header.employee-location') : '',
                width: '15%',
                isSortable: store.payload!.employeeLocations.length > 1 ? true : undefined,
            },
            {
                header: store.label('questions.ad-hoc.sub-header.send-date'),
                width: '15%',
                isSortable: true,
            },
        ],
        rows: rows.length > 0 ? rows : {
            type: 'info',
            text: store.label('questions.ad-hoc.no-data'),
        } as IDataInfoProps,
    });
};

const questionsFilterProps = (store:QuestionsStore): IFilterProps => ({
    label: 'Filter label',
    onChange: (value: TFilterValue): void => store.onQuestionsTabChange(value as QuestionsTab),
    value: store.variables.openTab,

    items: [
        {
            id: 'filter-1',
            name: 'filter',
            label: store.label('questions.filter.catalog-questions'),
            value: 'catalog-questions',
        },
        {
            id: 'filter-2',
            name: 'filter',
            label: store.label('questions.filter.ad-hoc-questions'),
            value: 'ad-hoc-questions',
        },
    ],
});

const addAdHocQuestionProps = (store: QuestionsStore): IButtonProps => ({
    text: store.label('questions.ad-hoc.add-question-button'),
    onClick: (): void => store.openAddAdHocQuestionModal(),
});

const addCatalogQuestionProps = (store: QuestionsStore): IButtonProps => ({
    text: store.label('questions.catalog-question.add-question-button'),
    onClick: (): void => store.openAddCatalogQuestionModal(),
});

const addCustomQuestionCategoryProps = (store: QuestionsStore): IButtonProps => ({
    text: store.label('categories.custom-category.add-category-button'),
    onClick: (): void => store.openAddCustomQuestionCategoryModal(),
});

export const questionTextInUserLanguage = (userStore: UserStore, questionLanguages: Languages): string => {
    switch (userStore.payload?.language) {
        case 'et':
            return questionLanguages.textEstonian!;
        case 'ru':
            return questionLanguages.textRussian!;
        case 'fi':
            return questionLanguages.textFinnish!;
        case 'lt':
            return questionLanguages.textLithuanian!;
        case 'lv':
            return questionLanguages.textLatvian!;
        case 'sv':
            return questionLanguages.textSwedish!;
        case 'pl':
            return questionLanguages.textPolish!;
        case 'ja':
            return questionLanguages.textJapanese!;
        case 'vi':
            return questionLanguages.textVietnamese!;
        default:
            return questionLanguages.textEnglish!;
    }
};
